/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { getFileNameInfo } from '@flowus/common';
import type { FC } from 'react';
import { Icon } from 'src/common/components/icon';
import { useOpenModal } from 'src/common/components/next-modal';
import { Ring } from 'src/common/components/ring';
import { Tooltip } from 'src/common/components/tooltip';
import { getReadableFileSizeString } from 'src/editor/utils/size-utils';
import { $downloadManager } from 'src/services/download-manager';
import { getFileIcon } from 'src/utils/file';
import { FileNameUiKit } from '../file-name-uikit';
import type { DownloadTask } from './types';

interface Props {
  task: DownloadTask;
}
/** 上传item */
export const DownloadItem: FC<Props> = ({ task }) => {
  const openModal = useOpenModal();
  const taskDetail = $downloadManager.tasks.find((it) => it.id === task.id)!;
  if (taskDetail == null) return null;

  const fileInfo = getFileNameInfo(taskDetail?.title);
  const progress = taskDetail.receivedBytes / taskDetail.totalBytes;
  return (
    <div
      className="group flex items-center justify-between animate-hover px-2"
      style={{
        height: taskDetail.state === 'completed' ? '40px' : '60px',
      }}
      onClick={() => {
        const { shell } = require('electron');
        shell.showItemInFolder(taskDetail.savePath);
      }}
    >
      <div className="flex items-center w-[65%]">
        <Icon name={getFileIcon(fileInfo.extName)} size={'middle'} className={'flex-shrink-0'} />
        <div className="ml-2 w-full">
          <FileNameUiKit className="w-full flex items-center text-t2" fileName={taskDetail.title} />
          {taskDetail.state === 'progressing' && (
            <div className="text-t4 text-grey3">
              {getReadableFileSizeString(taskDetail.totalBytes)}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center flex-shrink-0">
        {/* 下载中 */}
        {taskDetail.state === 'progressing' && (
          <>
            <div className="group-hover:hidden text-t2 text-grey3 ">
              下载
              {taskDetail.numItems > 1 ? `${taskDetail.itemIndex + 1}/${taskDetail.numItems}` : ''}
              中 {(progress * 100).toFixed(2)}%
            </div>
            <Ring
              percent={progress}
              className="group-hover:hidden border-grey7 ml-2"
              ringColorClassName="border-active_color"
            />
            <Tooltip popup="取消并从列表中移除">
              <Icon
                className="group-hover:flex hidden text-grey3"
                name={'IcNoAccess'}
                size="middle"
                onClick={async (event) => {
                  event.stopPropagation();
                  openModal.warning({
                    title: '确定取消该任务',
                    confirmText: '确定取消',
                    confirm: async () => {
                      await $downloadManager.cancel(task.id);
                      await $downloadManager.remove(task.id);
                    },
                  });
                }}
              />
            </Tooltip>
          </>
        )}
        {/* 下载成功 */}
        {taskDetail.state === 'completed' && (
          <>
            <div className="group-hover:hidden text-t2 text-grey3 ">下载完成</div>
            <div className="hidden group-hover:flex items-center">
              <Tooltip popup="打开文件所在页面">
                <Icon
                  name={'IcSearch'}
                  size="middle"
                  className="text-grey3"
                  onClick={(e) => {
                    e.stopPropagation();
                    const { shell } = require('electron');
                    shell.showItemInFolder(taskDetail.savePath);
                  }}
                />
              </Tooltip>
              <Tooltip popup="从列表中移除">
                <Icon
                  className="ml-2.5 text-grey3"
                  name={'IcNoAccess'}
                  size="middle"
                  onClick={async (event) => {
                    event.stopPropagation();
                    await $downloadManager.remove(task.id);
                  }}
                />
              </Tooltip>
            </div>
          </>
        )}
        {/* 下载成功 */}
        {(taskDetail.state === 'interrupted' || taskDetail.state === 'cancelled') && (
          <>
            <div className="group-hover:hidden text-t2 text-grey3 ">下载中断</div>
            <div className="hidden group-hover:flex items-center">
              <Tooltip popup="取消并从列表中移除">
                <Icon
                  className="group-hover:flex hidden text-grey3"
                  name={'IcNoAccess'}
                  size="middle"
                  onClick={async (event) => {
                    event.stopPropagation();
                    openModal.warning({
                      title: '确定取消该任务',
                      confirmText: '确定取消',
                      confirm: async () => {
                        await $downloadManager.cancel(task.id);
                        await $downloadManager.remove(task.id);
                      },
                    });
                  }}
                />
              </Tooltip>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
